/**
 * DoNextWhy — the opt-in "Why" behind one Do next step (kanban 1af16b1c).
 *
 * Jess (2026-10-01): the key points and the next steps up top, and for each
 * "you could click them to see the details of what it checked". Scott
 * (2026-10-06): keep the tiles terse, no hint repeated per item, no evidence
 * text on the page by default. So: one small toggle at the end of the step,
 * nothing else until it is pressed, and only on a step Merlin backed with a
 * `step_evidence` entry — a filing from before the field renders exactly as
 * it did. Open, it shows what Merlin checked, the identifiers it read (with
 * a signal's own words when a ref names one), and the cause-chain steps
 * that drive this action: the slice of "how we got here" behind it, not the
 * whole trace again (that stays folded once, by the timeline).
 */
import { useState } from 'react';

import { EvidenceRefs } from './EvidenceRefs';
import type { ChainStep, StepEvidence } from './merlinBrief';
import styles from './DoNextWhy.module.css';

const STATE_WORD: Record<ChainStep['state'], string> = {
  past: '',
  now: 'now',
  next: 'not yet',
};

export function DoNextWhy({
  why,
  from = [],
  signals = [],
  onToggle,
}: {
  why: StepEvidence;
  /** The chain steps that drive this action, oldest first. */
  from?: ReadonlyArray<{ key: string; when: string; title: string; state: ChainStep['state'] }>;
  signals?: ReadonlyArray<{ id: string; observation: string }>;
  /** Called with the new open state, for the usage trail. */
  onToggle?: (open: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <span className={styles.wrap}>
      <button
        type="button"
        className={styles.btn}
        aria-expanded={open}
        aria-label="Why this step"
        onClick={() => {
          const next = !open;
          setOpen(next);
          onToggle?.(next);
        }}
      >
        Why {open ? '▾' : '▸'}
      </button>
      {open && (
        <div className={styles.panel} data-testid="step-why">
          {why.basis && (
            <div className={styles.block}>
              <span className={styles.label}>Merlin checked</span>
              <span className={styles.text}>{why.basis}</span>
            </div>
          )}
          <EvidenceRefs refs={why.evidence} signals={signals} />
          {from.length > 0 && (
            <div className={styles.block}>
              <span className={styles.label}>Because</span>
              <ul className={styles.from} aria-label="How it got here">
                {from.map((s) => (
                  <li key={s.key} data-state={s.state}>
                    {s.when && <span className={styles.when}>{s.when}</span>}
                    <span>{s.title}</span>
                    {STATE_WORD[s.state] && <span className={styles.stateWord}>{STATE_WORD[s.state]}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </span>
  );
}

export default DoNextWhy;
